import { TerritoryBounds } from './typings'

export type ViewportBounds = {
  minLatitude: number
  maxLatitude: number
  minLongitude: number
  maxLongitude: number
}

/**
 * Axis-aligned overlap test between a territory's envelope and the current viewport
 * (WWM-17508).
 *
 * This is the whole reason the initial load asks for summaries: `bounds` comes back for every
 * territory, so working out which polygons the screen touches costs zero server calls. Measured
 * on 24 territories of ~600 vertices, fetching only the visible geometry was 4.7x lighter than
 * fetching all of it, and the gap widens with tenant size because this scales with what is on
 * screen rather than with how many territories exist.
 *
 * Deliberately inclusive at the edges: a territory touching the viewport border is visible, and
 * drawing it a moment early is far cheaper than a polygon popping in once it is already on screen.
 */
export function overlapsViewport(
  bounds: TerritoryBounds,
  viewport: ViewportBounds,
): boolean {
  return boundsIntersect(bounds, viewport)
}

/**
 * The same axis-aligned test between any two envelopes, named for the general case.
 *
 * `overlapsViewport` is one caller of this; overlap detection is the other, where it is the cheap
 * reject before turf does real polygon arithmetic. Two rectangles either miss on an axis or they
 * do not, so there is no second implementation here -- only a name that does not say "viewport"
 * when neither side is one.
 */
export function boundsIntersect(
  a: TerritoryBounds,
  b: TerritoryBounds,
): boolean {
  return !(
    a.maxLatitude < b.minLatitude ||
    a.minLatitude > b.maxLatitude ||
    a.maxLongitude < b.minLongitude ||
    a.minLongitude > b.maxLongitude
  )
}

/**
 * Territory ids that overlap the viewport and whose geometry is not already cached or in flight.
 * Returns [] when there is nothing new to ask for, so callers can skip the request entirely.
 */
export function selectIdsToFetch(
  summaries: { id: number; bounds: TerritoryBounds }[],
  viewport: ViewportBounds,
  cachedIds: Set<number>,
  loadingIds: Set<number>,
): number[] {
  return summaries
    .filter(
      (t) =>
        overlapsViewport(t.bounds, viewport) &&
        !cachedIds.has(t.id) &&
        !loadingIds.has(t.id),
    )
    .map((t) => t.id)
}
